import React, { useState } from 'react';
import { Plus, Trash2, Link as LinkIcon, ExternalLink, AlertCircle, ClipboardPaste, Upload } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Label } from './ui/label';
import BulkAddLinksModal from './BulkAddLinksModal';

/**
 * MultiLinkInput Component
 * Allows users to add, edit, and remove multiple links with labels
 */
const MultiLinkInput = ({ links = [], onChange, disabled = false }) => {
  const [newLink, setNewLink] = useState({ url: '', label: '' });
  const [urlError, setUrlError] = useState('');
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [bulkInitialTab, setBulkInitialTab] = useState('paste');

  // Validate URL format
  const isValidUrl = (url) => {
    if (!url) return true; // Empty is valid (optional)
    try {
      const urlObj = new URL(url);
      return urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
    } catch {
      return false;
    }
  };

  const normalizeUrl = (url) => url.trim().replace(/\/+$/, '').toLowerCase();

  // Add new link
  const handleAddLink = () => {
    if (!newLink.url.trim()) {
      setUrlError('Please enter a URL');
      return;
    }
    
    if (!isValidUrl(newLink.url)) {
      setUrlError('Please enter a valid URL (https://...)');
      return;
    }

    if (links.some((l) => normalizeUrl(l.url || '') === normalizeUrl(newLink.url))) {
      setUrlError('This link already exists in this task.');
      return;
    }

    const updatedLinks = [
      ...links,
      {
        url: newLink.url.trim(),
        label: newLink.label.trim() || 'Link'
      }
    ];
    onChange(updatedLinks);
    setNewLink({ url: '', label: '' });
    setUrlError('');
  };

  const openBulkModal = (tab) => {
    setBulkInitialTab(tab);
    setBulkModalOpen(true);
  };

  const handleBulkAdd = (newLinks) => {
    onChange([...links, ...newLinks]);
  };

  // Remove link at index
  const handleRemoveLink = (index) => {
    const updatedLinks = links.filter((_, i) => i !== index);
    onChange(updatedLinks);
  };

  // Update existing link
  const handleUpdateLink = (index, field, value) => {
    const updatedLinks = [...links];
    updatedLinks[index] = { ...updatedLinks[index], [field]: value };
    onChange(updatedLinks);
  };

  // Handle Enter key to add link
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddLink();
    }
  };

  return (
    <div className="space-y-3">
      <Label className="flex items-center gap-2">
        <LinkIcon size={14} />
        Links
      </Label>
      
      {/* Existing Links */}
      {links.length > 0 && (
        <div className="space-y-2">
          {links.map((link, index) => (
            <div 
              key={index} 
              className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg border border-slate-200"
            >
              <div className="flex-1 grid grid-cols-2 gap-2">
                <Input
                  value={link.label || ''}
                  onChange={(e) => handleUpdateLink(index, 'label', e.target.value)}
                  placeholder="Label (e.g., Design Doc)"
                  disabled={disabled}
                  className="text-sm h-8"
                  data-testid={`existing-link-label-${index}`}
                />
                <Input
                  value={link.url || ''}
                  onChange={(e) => handleUpdateLink(index, 'url', e.target.value)}
                  placeholder="https://..."
                  disabled={disabled}
                  className="text-sm h-8"
                  data-testid={`existing-link-url-${index}`}
                />
              </div>
              <a 
                href={link.url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="p-1.5 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded"
                title="Open link"
              >
                <ExternalLink size={16} />
              </a>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => handleRemoveLink(index)}
                disabled={disabled}
                className="p-1.5 h-auto text-red-500 hover:text-red-600 hover:bg-red-50"
                data-testid={`remove-link-${index}`}
              >
                <Trash2 size={16} />
              </Button>
            </div>
          ))}
        </div>
      )}

      {/* Add New Link */}
      <div className="space-y-2">
        <div className="flex items-start gap-2">
          <div className="flex-1 grid grid-cols-2 gap-2">
            <Input
              value={newLink.label}
              onChange={(e) => setNewLink({ ...newLink, label: e.target.value })}
              placeholder="Label (optional)"
              disabled={disabled}
              className="text-sm"
              data-testid="new-link-label"
            />
            <Input
              value={newLink.url}
              onChange={(e) => {
                setNewLink({ ...newLink, url: e.target.value });
                setUrlError('');
              }}
              onKeyPress={handleKeyPress}
              placeholder="https://..."
              disabled={disabled}
              className={`text-sm ${urlError ? 'border-red-300 focus:ring-red-500' : ''}`}
              data-testid="new-link-url"
            />
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleAddLink}
            disabled={disabled || !newLink.url.trim()}
            className="flex items-center gap-1 h-9"
            data-testid="add-link-btn"
          >
            <Plus size={16} />
            Add
          </Button>
        </div>
        
        {/* URL Error */}
        {urlError && (
          <div className="flex items-center gap-1 text-sm text-red-600">
            <AlertCircle size={14} />
            {urlError}
          </div>
        )}

        {/* Bulk Actions */}
        <div className="flex items-center gap-3 pt-0.5">
          <button
            type="button"
            onClick={() => openBulkModal('paste')}
            disabled={disabled}
            className="text-xs font-medium text-sky-600 hover:text-sky-700 flex items-center gap-1 disabled:opacity-50"
            data-testid="bulk-add-links-btn"
          >
            <ClipboardPaste size={12} /> Bulk Add Links
          </button>
          <button
            type="button"
            onClick={() => openBulkModal('upload')}
            disabled={disabled}
            className="text-xs font-medium text-sky-600 hover:text-sky-700 flex items-center gap-1 disabled:opacity-50"
            data-testid="upload-links-file-btn"
          >
            <Upload size={12} /> Upload File
          </button>
        </div>
      </div>

      {/* Empty State */}
      {links.length === 0 && (
        <p className="text-sm text-slate-500 italic">
          No links added. Add relevant URLs like documentation, designs, or resources.
        </p>
      )}

      <BulkAddLinksModal
        open={bulkModalOpen}
        onOpenChange={setBulkModalOpen}
        existingLinks={links}
        onAdd={handleBulkAdd}
        initialTab={bulkInitialTab}
      />
    </div>
  );
};

export default MultiLinkInput;
